import { useState, useRef, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

const FloatingInputWithNA = ({
    id,
    label,
    value,
    onChange,
    type = 'text',
    required = false,
    error,
    darkMode,
    small = false,
    name,
    disabled = false,
    min,
    max,
    placeholder,
    maxLength,
    step,
    inputMode,
    pattern,
    naOption = 'Not Available',
    extraOptions = [],
    onBlur,
    readOnly = false,
    ...rest
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [focusedOptionIndex, setFocusedOptionIndex] = useState(-1);
    const [inputValue, setInputValue] = useState('');
    const previousValueRef = useRef('');
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const optionRef = useRef(null);

    const availableOptions = [naOption, ...(Array.isArray(extraOptions) ? extraOptions : [])]
        .map((opt) => String(opt || '').trim())
        .filter(Boolean);
    const isSpecialOption = availableOptions.includes(String(value || '').trim());
    
    // Label always stays on border (always floating)
    const isLabelFloating = true;
    const showClearNA = isSpecialOption && !disabled;

    // Update input value when prop changes
    useEffect(() => {
        if (!isSpecialOption) {
            setInputValue(value || '');
        }
    }, [value, isSpecialOption]);

    // Check if should show dropdown based on input
    const shouldShowDropdown = useCallback(() => {
        if (!isFocused || isSpecialOption || disabled) return false;
        
        const trimmedValue = inputValue.trim();
        if (trimmedValue === '') return true; // Always show when empty and focused
        
        // Check if matches NA pattern (case-insensitive)
        const lowered = trimmedValue.toLowerCase();
        const naPattern = /^(?:n|na|n\/a)$/i;
        if (naPattern.test(trimmedValue)) return true;
        return availableOptions.some((opt) => opt.toLowerCase().startsWith(lowered));
    }, [inputValue, isFocused, isSpecialOption, disabled, availableOptions]);

    // Update dropdown visibility
    useEffect(() => {
        if (shouldShowDropdown()) {
            setShowDropdown(true);
        } else if (focusedOptionIndex === -1) {
            setShowDropdown(false);
        }
    }, [shouldShowDropdown, focusedOptionIndex]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
                setFocusedOptionIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSelectOption = (selectedOption) => {
        if (value && !availableOptions.includes(String(value).trim())) {
            previousValueRef.current = value;
        }
        // Include both name and value so generic change handlers work
        onChange({
            target: {
                name: name || id,
                value: selectedOption,
            },
        });
        setInputValue('');
        setShowDropdown(false);
        setFocusedOptionIndex(-1);
    };

    const handleClearNA = () => {
        if (previousValueRef.current) {
            onChange({
                target: {
                    name: name || id,
                    value: previousValueRef.current,
                },
            });
            setInputValue(previousValueRef.current);
            previousValueRef.current = '';
        } else {
            onChange({
                target: {
                    name: name || id,
                    value: '',
                },
            });
            setInputValue('');
        }

        setShowDropdown(false);
        setFocusedOptionIndex(-1);
        
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 0);
    };

    const handleInputChange = (e) => {
        if (!isSpecialOption) {
            const newValue = e.target.value;
            setInputValue(newValue);
            onChange(e);
            
            if (newValue !== '') {
                previousValueRef.current = newValue;
            }
        }
    };

    const handleFocus = () => {
        setIsFocused(true);
        if (!isSpecialOption && !disabled && !readOnly) {
            setShowDropdown(true);
        }
    };

    const handleBlur = (e) => {
        // Keep dropdown open when focus moves within this component
        const relatedTarget = e.relatedTarget;
        if (relatedTarget && dropdownRef.current && dropdownRef.current.contains(relatedTarget)) {
            return;
        }
        
        setTimeout(() => {
            setIsFocused(false);
            setShowDropdown(false);
            setFocusedOptionIndex(-1);
            onBlur?.(e);
        }, 100);
    };

    const handleKeyDown = (e) => {
        if (showDropdown) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setFocusedOptionIndex(0);
                if (optionRef.current) {
                    optionRef.current.focus();
                }
            }
            if (e.key === 'Escape') {
                setShowDropdown(false);
                setFocusedOptionIndex(-1);
            }
            if (e.key === 'Enter' && focusedOptionIndex >= 0) {
                e.preventDefault();
                handleSelectOption(availableOptions[focusedOptionIndex] || naOption);
            }
            if (e.key === 'Tab' && focusedOptionIndex >= 0) {
                e.preventDefault();
                handleSelectOption(availableOptions[focusedOptionIndex] || naOption);
            }
        }
        
        // If user presses Enter while typing NA, select it
        if (e.key === 'Enter' && shouldShowDropdown() && !showDropdown) {
            setShowDropdown(true);
        }
    };

    const handleOptionKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (focusedOptionIndex >= 0) {
                handleSelectOption(availableOptions[focusedOptionIndex] || naOption);
            }
        }
        if (e.key === 'Escape') {
            setShowDropdown(false);
            setFocusedOptionIndex(-1);
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            if (!availableOptions.length) return;
            const delta = e.key === 'ArrowDown' ? 1 : -1;
            const next = focusedOptionIndex < 0
                ? 0
                : (focusedOptionIndex + delta + availableOptions.length) % availableOptions.length;
            setFocusedOptionIndex(next);
        }
        if (e.key === 'Tab') {
            e.preventDefault();
            if (focusedOptionIndex >= 0) {
                handleSelectOption(availableOptions[focusedOptionIndex] || naOption);
            }
        }
    };

    const handleOptionMouseEnter = (index) => {
        setFocusedOptionIndex(index);
    };

    const handleOptionMouseLeave = () => {
        setFocusedOptionIndex(-1);
    };

    const handleOptionFocus = (index) => {
        setFocusedOptionIndex(index);
    };

    const handleOptionBlur = () => {
        // Only blur option if not going to input
        setTimeout(() => {
            if (document.activeElement !== inputRef.current) {
                setFocusedOptionIndex(-1);
            }
        }, 0);
    };

    return (
        <div className="relative mb-4 w-full min-w-0">
            <div className="relative w-full min-w-0" ref={dropdownRef}>
                <input
                    ref={inputRef}
                    id={id}
                    name={name || id}
                    type={type}
                    value={isSpecialOption ? '' : inputValue}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    required={required && !isSpecialOption}
                    disabled={disabled}
                    readOnly={readOnly || isSpecialOption}
                    min={min}
                    max={max}
                    placeholder={isSpecialOption ? '' : placeholder}
                    maxLength={maxLength}
                    step={step}
                    inputMode={inputMode}
                    pattern={pattern}
                    style={{
                        paddingRight:
                            isSpecialOption && showClearNA && !disabled
                                ? '2.25rem'
                                : undefined,
                    }}
                    className={`block w-full rounded-none border-0 border-b px-3 py-3.5 text-sm text-left placeholder:text-transparent
                        ${error ? 'border-b border-red-500' : isFocused ? 'border-b border-redhalo-red' : 'border-b border-gray-300'}
                        focus:outline-none focus:ring-0
                        ${!error && !disabled && !isSpecialOption ? 'hover:border-b-redhalo-navy' : ''}
                        bg-white ${disabled ? 'cursor-not-allowed text-gray-500 disabled:bg-white' : isSpecialOption ? 'cursor-default text-gray-900' : 'text-gray-900'}
                        `}
                    {...rest}
                />
                {isSpecialOption && (
                <div
                    className={`pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 items-center ${showClearNA && !disabled ? 'right-10' : 'right-3'}`}
                    aria-hidden
                >
                    <span className="w-full truncate text-left text-sm text-gray-900">
                        {String(value || '')}
                    </span>
                </div>
                )}
                {isSpecialOption && showClearNA && (
                                <button
                                    type="button"
                                    onClick={handleClearNA}
                                    className="absolute right-2 top-1/2 z-10 -translate-y-1/2 text-red-500 hover:text-red-700 focus:outline-none p-1"
                                    title="Clear NA"
                                >
                                    <X size={16} />
                                </button>
                )}

                {/* NA Dropdown - Auto-suggest when typing N or NA */}
                {showDropdown && !disabled && !isSpecialOption && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-sm shadow-lg z-20">
                        {availableOptions.map((opt, idx) => (
                            <div
                                key={`${opt}-${idx}`}
                                ref={idx === 0 ? optionRef : undefined}
                                tabIndex={0}
                                className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between focus:outline-none
                                    ${focusedOptionIndex === idx ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                                onClick={() => handleSelectOption(opt)}
                                onMouseEnter={() => handleOptionMouseEnter(idx)}
                                onMouseLeave={handleOptionMouseLeave}
                                onKeyDown={handleOptionKeyDown}
                                onFocus={() => handleOptionFocus(idx)}
                                onBlur={handleOptionBlur}
                            >
                                <span>{opt}</span>
                                {focusedOptionIndex === idx && (
                                    <span className="text-xs">↵</span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            
            <label
                htmlFor={id}
                className={`absolute text-sm 
                    ${error ? 'text-red-500' : isFocused ? 'text-redhalo-navy' : 'text-gray-600'}
                    duration-300 transform 
                    ${'-translate-y-4 scale-75' }
                    top-2 z-10 origin-[0]
                    ${'bg-white'} px-2 left-1 rounded-none`}
            >
                {label}
                {required && !isSpecialOption && <span className="text-red-500"> *</span>}
            </label>

            {error ? (
                <p className="mt-0.5 text-xs text-red-500" aria-live="polite">
                    {error}
                </p>
            ) : null}
        </div>
    );
};

export default FloatingInputWithNA;